import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import { SUBCATEGORIES_COLLECTION } from '@/modules/inventory/services/constants';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import type { Subcategory, SubcategoryInput } from '@/modules/inventory/types';
import type { UserProfile } from '@/types/user';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type SubcategoryDoc = {
  companyId: string;
  categoryId: string;
  name: string;
  description: string;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapSubcategory(id: string, data: SubcategoryDoc): Subcategory {
  return {
    id,
    companyId: data.companyId,
    categoryId: data.categoryId,
    name: data.name,
    description: data.description,
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listSubcategories(
  companyId: string = appConfig.companyId,
  categoryId?: string,
): Promise<Subcategory[]> {
  const q = categoryId
    ? query(
        collection(getFirestoreDb(), SUBCATEGORIES_COLLECTION),
        where('companyId', '==', companyId),
        where('categoryId', '==', categoryId),
      )
    : query(
        collection(getFirestoreDb(), SUBCATEGORIES_COLLECTION),
        where('companyId', '==', companyId),
      );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapSubcategory(d.id, d.data() as SubcategoryDoc))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function saveSubcategory(
  input: SubcategoryInput,
  id?: string,
  profile?: UserProfile | null,
): Promise<Subcategory> {
  return withFirestoreError(async () => {
    const isNew = !id;
    const ref = isNew
      ? doc(collection(getFirestoreDb(), SUBCATEGORIES_COLLECTION))
      : doc(getFirestoreDb(), SUBCATEGORIES_COLLECTION, id);
    const existing = isNew ? null : await getDoc(ref);
    const before = existing?.exists()
      ? toAuditSnapshot(
          mapSubcategory(existing!.id, existing!.data() as SubcategoryDoc),
        )
      : undefined;
    const payload = {
      companyId: appConfig.companyId,
      categoryId: input.categoryId,
      name: input.name.trim(),
      description: input.description.trim(),
      active: input.active,
      ...buildTimestampPayload(isNew),
    };
    await setDoc(ref, payload, { merge: true });
    const saved = await getDoc(ref);
    if (!saved.exists()) throw new Error('No se pudo guardar la subcategoría.');
    const result = mapSubcategory(saved.id, saved.data() as SubcategoryDoc);
    recordEntityWrite({
      entityType: 'subcategory',
      entityId: result.id,
      isCreate: isNew,
      ...(before !== undefined ? { before } : {}),
      after: toAuditSnapshot(result),
    });
    return result;
  }, 'subcategory', profile);
}
